import React from 'react';
import { CheckCircle2, Clock, FileCheck, Users } from 'lucide-react';
import AnimateOnScroll from './AnimateOnScroll';
import { solutionSection } from '../content/landingContent';

export const SolutionSection = () => {
  return (
    <section className="py-24 lg:py-32 bg-stone-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <AnimateOnScroll>
            <div className="space-y-8">
              <div className="space-y-6">
                <h2 className="text-2xl lg:text-3xl font-bold text-stone-900 leading-tight tracking-tight">
                  {solutionSection.headline}
                </h2>
                <p className="text-lg text-stone-600 leading-relaxed">
                  {solutionSection.subheadline}
                </p>
              </div>
              <div className="space-y-6 pt-4">
                {solutionSection.benefits.map((benefit, index) => (
                  <div key={index} className="space-y-2 group">
                    <div className="flex items-start space-x-4">
                      <CheckCircle2 className="w-6 h-6 text-slate-600 flex-shrink-0 mt-1 group-hover:text-slate-700 transition-all duration-200" />
                      <div className="space-y-1">
                        <p className="text-lg font-semibold text-stone-900 leading-relaxed">
                          {benefit.title}
                        </p>
                        <p className="text-base text-stone-600 leading-relaxed">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AnimateOnScroll>
          
          <AnimateOnScroll delay={200}>
            <div className="relative">
              {/* Enhanced mockup with more details */}
              <div className="bg-white rounded-lg shadow-sm p-8 space-y-6 border border-stone-200">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 border-b border-stone-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-stone-900">{solutionSection.mockupData.projectName}</p>
                      <p className="text-xs text-stone-500">Client: {solutionSection.mockupData.clientName}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-xs text-stone-600">Active</span>
                  </div>
                </div>
                
                {/* Progress indicator */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-stone-600">
                    <span>Project Progress</span>
                    <span>{solutionSection.mockupData.progress}%</span>
                  </div>
                  <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div className="w-2/3 h-full bg-slate-600 rounded-full"></div>
                  </div>
                </div>

                {/* Approval items */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-stone-900">Floor plan approved</p>
                        <p className="text-xs text-stone-500">Approved 2 days ago</p>
                      </div>
                    </div>
                    <FileCheck className="w-4 h-4 text-green-600" />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="text-sm font-medium text-stone-900">Material selection pending</p>
                        <p className="text-xs text-stone-500">Awaiting client response</p>
                      </div>
                    </div>
                    <div className="w-5 h-5 rounded-full border-2 border-amber-400"></div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg border border-stone-200">
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-stone-600" />
                      <div>
                        <p className="text-sm font-medium text-stone-900">Lighting fixtures pending</p>
                        <p className="text-xs text-stone-500">Review in progress</p>
                      </div>
                    </div>
                    <div className="w-5 h-5 rounded-full border-2 border-stone-400"></div>
                  </div>
                </div>
                
                {/* Footer stats */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-stone-200">
                  <div className="text-center">
                    <p className="text-xl font-bold text-slate-600">{solutionSection.mockupData.approvedItems}</p>
                    <p className="text-xs text-stone-500">Approved</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-amber-600">{solutionSection.mockupData.pendingItems}</p>
                    <p className="text-xs text-stone-500">Pending</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-stone-400">{solutionSection.mockupData.revisedItems}</p>
                    <p className="text-xs text-stone-500">Revised</p>
                  </div>
                </div>
              </div>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;